/*
SF2 Synthesizer

Receive MIDI events and create WebAudio nodes.

Resources:

http://basicsynth.com/uploads/SF2-DLS.pdf
https://github.com/gree/sf2synth.js/blob/master/src/sound_font_synth_note.js
https://www.native-instruments.com/forum/threads/sf2-mono-samples-to-stereo.37966/#post-239817
https://github.com/davy7125/polyphone/blob/master/sources/sound_engine/modulatedparameter.cpp

Status: Active, copied from audio-builder and chlorian, updating here.
*/

DBWorklet from ./db-worklet.civet
ReverbWorklet from ./reverb-worklet.civet
ChannelSynth from ./channel.civet

export DBWorklet
export ReverbWorklet
export { createReverb, REVERB_RT60, REVERB_DRAIN_SECONDS } from ./reverb.civet
export { createChorusNode } from ./chorus.civet

/**
Optional synth features. (The reverb and chorus buses are engine-standard, not options —
a song that never sends into them stays dry.)
*/
export type SF2SynthOptions = {
  /**
  How many dB one unit of the initialAttenuation generator is worth. The SF2 spec says
  centibels (`SPEC_ATTENUATION`, 0.1 dB), but the original E-MU hardware measured ~0.4x
  that, and FluidSynth and SpessaSynth both ship the same 0.4x for compatibility — so
  `EMU_ATTENUATION` (0.04 dB) is the de-facto standard banks are balanced against, and
  the default here. Override per file for a bank authored strictly to the spec — with
  the wrong factor, heavily-attenuated zones drop out of (or jump out of) the mix:
  measured on GeneralUser GS 2.0.3, the spec factor sinks the square lead ~17 dB.
  */
  attenuationDbPerUnit?: number
}

/** initialAttenuation dB per unit as the SF2 spec defines it (centibels). */
export SPEC_ATTENUATION := 0.1
/**
initialAttenuation dB per unit as the original E-MU hardware applied it — and, via their
0.4x compatibility scaling, as FluidSynth and SpessaSynth apply it too. The default.
*/
export EMU_ATTENUATION := 0.04

type { MidiChannel } from ../midi/channel-data.civet
{ SF2Parser } from ./parser.civet
{ soundBankFormat } from ./soundbank-format.civet
export { soundBankFormat, readSoundBankName, type SoundBankFormat } from ./soundbank-format.civet
type { BagModGen } from ./parser.civet

/**
SF2Parser is re-exported because tooling outside the synth needs to READ a soundfont
without instantiating one: auditing which (bank, program) pairs a MIDI demands against what
a font actually carries, listing generators, grafting samples between fonts.

Three scripts under scripts/social/ each hand-rolled their own RIFF/pdta/phdr walk to do
exactly that, for the same reason the MIDI varint ended up duplicated three times — the
capable implementation was in lib but the package did not surface it. Importing the class
was never possible; only `SF2Synth` and the sample helpers were reachable.
*/
export { SF2Parser }

{ PI, cos, log10, log2, max, min, pow, sin } := Math
{ urlSafeSHA256 } from ../util.civet

SEMITONE := pow(2, 1/12)

{ clamp, remove } from ../util.civet
{ panToFloat } from ../midi/util.civet
{ assign } := Object

// REFERENCE_FREQ * pow(2, 6900 / 1200) = 440 Hz
REFERENCE_FREQ := 8.17579891565

NEAR_ZERO := pow(2, -16)

// buffer is an ArrayBuffer of an sf2 file.
export class SF2Synth extends ChannelSynth
  //@ts-expect-error
  banks: InstrumentSample[][][][]
  //@ts-expect-error
  drumBank: InstrumentSample[][][]
  //@ts-expect-error
  defaultBank: InstrumentSample[][][]
  name: string?
  sha: string?
  //@ts-expect-error
  buffer: ArrayBuffer

  // Preparing flag so we can skip playing notes while channel data syncs up by
  // replaying events from latest state snapshot
  preparing = false

  attenuationDbPerUnit = EMU_ATTENUATION

  @(buffer?: ArrayBuffer, options?: SF2SynthOptions)
    super()

    @attenuationDbPerUnit = options?.attenuationDbPerUnit ?? EMU_ATTENUATION

    @load(buffer) if buffer

  /**
  Register every worklet this synth needs on a context. Must resolve before `bind`, which is
  when the reverb bus is created.
  */
  prepareContext(context: BaseAudioContext)
    Promise.all [
      DBWorklet.prepareContext context
      ReverbWorklet.prepareContext context
    ]

  load(source: string | ArrayBuffer, options?: {attenuationDbPerUnit?: number})
    if source !<? ArrayBuffer
      source = fetch(source) |> await |> .arrayBuffer() |> await

    if options?.attenuationDbPerUnit?
      @attenuationDbPerUnit = options.attenuationDbPerUnit

    @buffer = source
    // SF3 parses as sfbk but its samples are Ogg Vorbis, which this parser would play
    // as PCM noise; DLS fails on the signature. Both belong to the SpessaSynth engine.
    format := soundBankFormat source
    if format is "sf3" or format is "dls"
      throw new Error `SF2Synth plays SF2 only; this bank is ${format.toUpperCase()}, which the SpessaSynth engine (the default) reads`
    parser := new SF2Parser(new Uint8Array(@buffer))
    parser.parse()

    instruments := parser.getInstruments()
    @banks = createAllInstruments(parser, instruments, @attenuationDbPerUnit)

    @defaultBank = @banks[0]
    @drumBank = @banks[128] or @defaultBank

    @sha = await urlSafeSHA256 @toBlob()
    @name = parser.name

    return this

  noteOn(channel: MidiChannel, n: number, v: number, t: number)
    return if @preparing

    {bank: bankIndex, id: c, program} := channel

    let instrument, voice

    // TODO: handle bank selects for all channels. Will require doing data
    // analysis on collection of MIDI files and figuring out whether they are
    // GM, GS, XG or others and then use that information to have the proper
    // bank select behavior
    if c is 9 // Drum Kit (Ch. 10)
      instrument = @drumBank[program]?[n] or @drumBank[0][n]
    else
      // GS semantics: a variation bank the soundfont doesn't carry falls back to the
      // capital tone (bank 0), the way Sound Canvas hardware resolves it — not to
      // silence. SC-88Pro-authored MIDI (e.g. ZUN's) selects variations liberally, and
      // dropping those notes muted whole channels (a bass line in th06_03).
      instrument = (@banks[bankIndex] or @defaultBank)[program]?[n] or @defaultBank[program]?[n]

    if instrument and @context
      samples .= samplesForVelocity instrument, v
      return unless samples.length
      // GS sound-controller NRPNs and the modwheel, as copy-on-write adjustments of the
      // zone records — the factory then builds (or skips) filters and LFOs from the
      // adjusted values exactly as it would for a soundfont that shipped them.
      samples = adjustSamples samples, channel
      if c is 9
        samples = adjustDrumSamples samples, channel, n

      // Exclusive class (sf2 spec p. 40): a new note in a non-zero exclusive
      // class shuts off sounding notes in the same class (hi-hats)
      exclusiveClasses := new Set samples.map(&.exclusiveClass).filter(& > 0)
      if exclusiveClasses.size
        @chokeVoices c, exclusiveClasses, t

      voice = noteOn t, samples, v, channel, @context
      @addVoice voice, c, n
    else
      console.warn `No instrument for bank: ${bankIndex} program: ${program} note: ${n}`

  noteOff(channel: MidiChannel, n: number, v: number, t: number)
    return if @preparing

    // Sustain pedal (CC 64): the key is up but the damper is off — the voice keeps
    // ringing at its sustain level and releases when the pedal lifts.
    if channel.sustain
      @sustainVoice channel.id, n
      return

    voice := @removeVoice(channel.id, n)
    if voice and @context
      noteOff(t, voice, @context)

  // CC 123-127: release every sounding key through its normal release
  // envelope, as if a noteOff arrived for each. With the sustain pedal down
  // the keys transfer to the pedal instead (GM: held notes keep sounding
  // until the pedal lifts); voices already on the pedal are untouched.
  allNotesOff(channel: MidiChannel, t: number)
    return if @preparing

    context := @context
    return unless context

    { voices, sustained } := @channelOut[channel.id]
    if channel.sustain
      voices.forEach ({ voice }) => sustained.push voice
    else
      voices.forEach ({ voice }) => noteOff t, voice, context
    voices.length = 0

  controlChange(channel: MidiChannel, n: number, v: number, t: number)
    super.controlChange channel, n, v, t
    // Pedal up: everything it was holding releases together. The handler runs before
    // channel.control updates, so the fresh pedal value is `v`, not channel.sustain.
    if n is 0x40 and v < 64 and @context
      context := @context
      @takeSustained(channel.id).forEach (voice) -> noteOff t, voice, context

  // Release voices on channel c that sound in one of the given exclusive
  // classes. Matches FluidSynth fluid_voice_kill_excl(): the choked voice
  // enters its release phase with vol and mod envelope release overridden to
  // -200 timecents rather than being cut.
  chokeVoices(c: number, exclusiveClasses: Set<number>, t: number)
    context := @context
    return unless context

    { voices } := @channelOut[c]

    voices.slice().forEach (entry) =>
      { voice } := entry
      if voice.instrumentSamples.some (s: InstrumentSample) => exclusiveClasses.has s.exclusiveClass
        noteOff t, voice, context, CHOKE_RELEASE
        remove voices, entry

  toBlob()
    new Blob [@buffer!],
      type: "application/octet-stream"

contextBufferCache := (context: BaseAudioContext & { _cache?: Map<Int16Array, AudioBuffer>}, buffer: Int16Array, sampleRate: number) ->
  cache := (context._cache ?= new Map<Int16Array, AudioBuffer>())

  audioBuffer .= cache.get buffer

  if !audioBuffer
    audioBuffer = context.createBuffer 1, buffer.length, sampleRate

    audioData := audioBuffer.getChannelData(0)
    buffer.forEach (n, i) ->
      audioData[i] = n / 32768

    cache.set buffer, audioBuffer

  return audioBuffer

createAllInstruments := (parser: SF2Parser, instruments: ReturnType<typeof parser.getInstruments>, attenuationDbPerUnit: number = EMU_ATTENUATION) ->
  banks: InstrumentSample[][][][] := []
  presets := parser.getPresets()

  presets.forEach (preset, _i) ->
    return if preset.name is "EOP"

    presetNumber := preset.header.preset
    // select bank
    if (banks[preset.header.bank] is undefined)
      banks[preset.header.bank] = []

    bank: InstrumentSample[][][] := banks[preset.header.bank]
    bank[presetNumber] = []
    // @ts-ignore TODO
    bank[presetNumber].name = preset.name

    let globalPresetGenerator: BagModGen?
    preset.info.forEach (info, i) ->
      presetGenerator .= info.generator
      instrumentId := getModGenAmount(presetGenerator, 'instrument', null)

      if i is 0 and !instrumentId?
        globalPresetGenerator = presetGenerator
      else
        instrument := instruments[instrumentId!]
        presetGenerator = assign {}, globalPresetGenerator, presetGenerator

        if instrument
          let globalInstrumentGenerator: BagModGen?
          instrument.info.forEach ({generator}, j) ->
            sampleID := getModGenAmount(generator, 'sampleID', null)

            if j is 0 and !sampleID?
              globalInstrumentGenerator = generator
            else
              if sampleID?
                instrumentGenerator := assign({}, globalInstrumentGenerator, generator)
                createNoteInfo(parser, presetGenerator, instrumentGenerator, bank[presetNumber], attenuationDbPerUnit)
            return
        else
          console.warn "no instrument ##{info.instrument}"
        return

  return banks

// https://www.polyphone-soundfonts.com/forum/soundfonts-help/29-understanding-attenuation?start=%1$d#122
// db = initialAttenuation * attenuationDbPerUnit — 0.1 per the spec (default), 0.04 as
// the E-MU hardware measured; see SF2SynthOptions.attenuationDbPerUnit.
// gain.gain = pow(10, -db/20)

addModGen := (presetGenerator: BagModGen, instrumentGenerator: BagModGen, param: GeneratorEnumerator, defaultValue?: number) ->
  // Preset generator default value is to leave the parameter unchanged, so 0
  getModGenAmount(presetGenerator, param, 0) +
  getModGenAmount(instrumentGenerator, param, defaultValue)

// dB Attenuation expressed as amplitude ratio (gain.value)
dbAttenuationToGain := (db: number) ->
  pow(10, -db / 20)

timecent := (value: number) ->
  // 16 bit int min represents zero delay (sf2 spec p. 34 attackVolEnv)
  if value <= -32768
    return 0

  pow(2, value / 1200)

defaultMinTime := timecent -12000

// FluidSynth fluid_voice_kill_excl() speeds up both envelope releases to
// -200 timecents when a voice is choked by its exclusive class
CHOKE_RELEASE := timecent -200

createNoteInfo := (parser: SF2Parser, presetGenerator: BagModGen, generator: BagModGen, preset: InstrumentSample[][], attenuationDbPerUnit: number = EMU_ATTENUATION) ->
  return unless generator.keyRange

  // 5-9
  modLfoToPitch   := addModGen(presetGenerator, generator, 'modLfoToPitch')
  vibLfoToPitch   := addModGen(presetGenerator, generator, 'vibLfoToPitch')
  modEnvToPitch   := addModGen(presetGenerator, generator, 'modEnvToPitch') / 100
  initialFilterFc := addModGen(presetGenerator, generator, 'initialFilterFc', 13500)
  initialFilterQ  := addModGen(presetGenerator, generator, 'initialFilterQ') / 10 // cB -> dB

  // 10-14
  modLfoToFilterFc := addModGen(presetGenerator, generator, 'modLfoToFilterFc')
  modEnvToFilterFc := addModGen(presetGenerator, generator, 'modEnvToFilterFc')
  // 12 endAddrsCoarseOffset (handled below)
  modLfoToVolume := addModGen(presetGenerator, generator, 'modLfoToVolume') / 10 // cB -> dB
  // 14 unused

  // 15, 16
  // Both are 0.1% units, i.e. 0-1000 maps to a 0-1 send level (sf2 spec p. 40). The spec
  // defines them only as a level "to the chorus/reverb effects processor" and mandates no
  // algorithm, so this is the whole of what the bank gets to say.
  chorusEffectsSend := clamp (addModGen(presetGenerator, generator, 'chorusEffectsSend')) / 1000, 0, 1
  reverbEffectsSend := clamp (addModGen(presetGenerator, generator, 'reverbEffectsSend')) / 1000, 0, 1

  // 17
  // Deliberately unclamped: fonts use out-of-range preset offsets (e.g. ±1000)
  // to pin a zone hard so channel pan can't move it. FluidSynth clamps only
  // the final sum of generator + CC pan, so we keep the raw value here and
  // clamp once per voice.
  pan := addModGen(presetGenerator, generator, 'pan') / 500

  // 18,19,20 unused

  // 21, 22
  delayModLFO := addModGen(presetGenerator, generator, 'delayModLFO', -12000)
  freqModLFO  := addModGen(presetGenerator, generator, 'freqModLFO')

  // 23, 24
  delayVibLFO := addModGen(presetGenerator, generator, 'delayVibLFO', -12000)
  freqVibLFO  := addModGen(presetGenerator, generator, 'freqVibLFO')

  // 25-30
  modDelay   := addModGen(presetGenerator, generator, 'delayModEnv',   -12000)
  modAttack  := addModGen(presetGenerator, generator, 'attackModEnv',  -12000)
  modHold    := addModGen(presetGenerator, generator, 'holdModEnv',    -12000)
  modDecay   := addModGen(presetGenerator, generator, 'decayModEnv',   -12000)
  modSustain := clamp addModGen(presetGenerator, generator, 'sustainModEnv') / 10, 0, 100 // % attenuation, spec range
  modRelease := addModGen(presetGenerator, generator, 'releaseModEnv', -12000)

  // 31 keynumToModEnvHold
  keynumToModEnvHold := addModGen(presetGenerator, generator, 'keynumToModEnvHold', 0)
  // 32 keynumToModEnvDecay
  keynumToModEnvDecay := addModGen(presetGenerator, generator, 'keynumToModEnvDecay', 0)

  // 33-38
  volDelay   := addModGen(presetGenerator, generator, 'delayVolEnv',   -12000)
  volAttack  := addModGen(presetGenerator, generator, 'attackVolEnv',  -12000)
  volHold    := addModGen(presetGenerator, generator, 'holdVolEnv',    -12000)
  volDecay   := addModGen(presetGenerator, generator, 'decayVolEnv',   -12000)
  // dB attenuation, bounded to the spec range (0..1440 cB, 8.1.3). A preset-level offset
  // can push the instrument+preset sum below zero, and since volDecay scales by this it
  // would go negative too — a negative envelope time is rejected by the AudioParam.
  volSustain := clamp addModGen(presetGenerator, generator, 'sustainVolEnv') / 10, 0, 144
  volRelease := addModGen(presetGenerator, generator, 'releaseVolEnv', -12000)

  // 39 keynumToVolEnvHold
  keynumToVolEnvHold := addModGen(presetGenerator, generator, 'keynumToVolEnvHold', 0)
  // 40 keynumToVolEnvDecay
  keynumToVolEnvDecay := addModGen(presetGenerator, generator, 'keynumToVolEnvDecay', 0)

  // 41 instrument (handled at preset)
  // 42 reserved1

  // 43 keyRange (preset zone range restricts the instrument zone range — GS drum kits
  // layer wide sub-instruments and carve them up at the preset level, so skipping the
  // intersection stacks every kit layer onto every key)
  // @ts-ignore TODO
  {lo, hi} := intersectRanges presetGenerator.keyRange, generator.keyRange
  return if lo > hi
  // 44 velRange (preset zone range restricts the instrument zone range)
  velRange := intersectRanges presetGenerator.velRange, generator.velRange
  return if velRange.lo > velRange.hi

  // 45 startAddrsCoarseOffset (handled below)

  // 46+@ keynum
  keynum := getModGenAmount(generator, 'keynum', null) // MIDI keynum 0-127
  // 47+@ velocity
  velocity := getModGenAmount(generator, 'velocity', null) // MIDI velocity 0-127

  // 48
  // The spec range is 0..1440 (attenuate only), and FluidSynth clamps to it. We accept
  // negative values as a boost: sf2-graft --boost-key writes them to correct kit-internal
  // balance measured against hardware, and a rip whose sample already peaks at 0 dBFS has
  // no other place to encode "this voice is N dB hotter on the real unit". Units follow
  // attenuationDbPerUnit like every other value of this generator (0.04 dB by default).
  initialAttenuation := clamp addModGen(presetGenerator, generator, 'initialAttenuation'), -960, 1440

  // 51-52
  tune :=
    addModGen(presetGenerator, generator, 'coarseTune') +
    addModGen(presetGenerator, generator, 'fineTune') / 100

  // 53
  sampleId     := getModGenAmount(generator, 'sampleID')
  sampleHeader := parser.sampleHeader![sampleId]
  {
    endLoop
    originalPitch
    pitchCorrection
    sampleRate
    startLoop
  } := sampleHeader
  sample: Int16Array := parser.samples![sampleId]

  // 54+@
  sampleModes := getModGenAmount(generator, 'sampleModes')

  // 55 reserved3

  // 56@
  scaleTuning := addModGen(presetGenerator, generator, 'scaleTuning', 100) / 100 // semitones / note

  // 57+@ exclusiveClass
  exclusiveClass := getModGenAmount(generator, 'exclusiveClass', 0)

  // 58+@
  overridingRootKey := getModGenAmount(generator, 'overridingRootKey', originalPitch as number)

  // SF2 spec 8.1.3 initialFilterQ / FluidSynth fluid_iir_filter: resonance is
  // paired with the passband lowered by half of Q, so a resonant zone keeps
  // its overall level (the noteOn filter condition must match this one)
  filterQCompensation :=
    if initialFilterQ > 0 or initialFilterFc < 13500
      pow(10, -max(initialFilterQ, 0) / 40)
    else
      1

  for i of [lo!..hi!]
    preset[i] ?= []
    n := keynum ?? i

    // key scaling
    ks := n - 60

    // Multiple samples
    preset[i].push
      velRange: velRange
      sample: sample
      sampleRate: sampleRate
      sampleModes: sampleModes
      basePlaybackRate: pow(
        SEMITONE,
        (n - overridingRootKey) * scaleTuning + tune + (pitchCorrection / 100)
      ),
      exclusiveClass: exclusiveClass
      // converted from dB to amplitude ratio
      initialAttenuation: pow(10, -initialAttenuation * attenuationDbPerUnit / 20) * filterQCompensation
      startTime: (
        getModGenAmount(generator, 'startAddrsCoarseOffset') * 32768 +
        getModGenAmount(generator, 'startAddrsOffset')
      ) / sampleRate,
      end: (
        getModGenAmount(generator, 'endAddrsCoarseOffset') * 32768 +
        getModGenAmount(generator, 'endAddrsOffset')
      ) / sampleRate,
      loopStart: (
        startLoop +
        getModGenAmount(generator, 'startloopAddrsCoarseOffset') * 32768 +
        getModGenAmount(generator, 'startloopAddrsOffset')
      ) / sampleRate,
      loopEnd: (
        endLoop +
        getModGenAmount(generator, 'endloopAddrsCoarseOffset') * 32768 +
        getModGenAmount(generator, 'endloopAddrsOffset')
      ) / sampleRate,
      volDelay:   timecent volDelay
      volAttack:  timecent volAttack
      volHold:    timecent volHold + keynumToVolEnvHold * ks
      // decay is in terms of 100% attenuation so a volSustain of 0 means decay
      // time is zero
      // TODO: does this depend on velocity and if so it may need to move into
      // noteOn handler
      volDecay:   timecent(volDecay + keynumToVolEnvDecay * ks) * (volSustain / 100)
      volSustain: dbAttenuationToGain(volSustain) // dB -> amplitude ratio
      sustainVolEnv: volSustain
      volRelease: timecent volRelease
      modDelay:   timecent modDelay
      modAttack:  timecent modAttack
      modHold:    timecent modHold + keynumToModEnvHold * ks
      modDecay:   timecent(modDecay + keynumToModEnvDecay * ks) * (modSustain / 100)
      modSustain: modSustain
      modRelease: timecent modRelease
      initialFilterFc: initialFilterFc
      initialFilterQ: initialFilterQ
      modEnvToFilterFc: modEnvToFilterFc

      overrideVelocity: velocity
      modLfoToPitch: modLfoToPitch // cents fs (full scale)
      vibLfoToPitch: vibLfoToPitch // cents fs
      modEnvToPitch: modEnvToPitch // cents fs
      modLfoToFilterFc: modLfoToFilterFc
      modLfoToVolume: modLfoToVolume
      chorusEffectsSend: chorusEffectsSend
      reverbEffectsSend: reverbEffectsSend
      pan: pan
      delayModLFO: timecent(delayModLFO)
      freqModLFO:  freqModLFO // absolute cents
      delayVibLFO: timecent(delayVibLFO)
      freqVibLFO:  freqVibLFO // absolute cents

      // debugging
      presetGenerator: presetGenerator
      generator: generator

  return

type getModGenAmountType = {
  (generator: BagModGen, enumeratorType: GeneratorEnumerator, defaultValue: null): number | null
  (generator: BagModGen, enumeratorType: GeneratorEnumerator, defaultValue?: number): number
}

//@ts-ignore
getModGenAmount: getModGenAmountType := (generator: BagModGen, enumeratorType: GeneratorEnumerator, defaultValue?: number | null) ->
  if defaultValue is undefined
    defaultValue = 0

  generator[enumeratorType]?.amount ?? defaultValue

amountToFreq := (val: number) ->
  pow(2, (val - 6900) / 1200) * 440

FULL_RANGE := { lo: 0, hi: 127 }

intersectRanges := (a: { lo?: number, hi?: number } = FULL_RANGE, b: { lo?: number, hi?: number } = FULL_RANGE) ->
  lo: max(a.lo ?? 0, b.lo ?? 0)
  hi: min(a.hi ?? 127, b.hi ?? 127)

// Only samples whose velRange includes the played velocity should sound
// (sf2 spec p. 39 velRange)
export samplesForVelocity := (samples: InstrumentSample[], v: number) ->
  samples.filter ({velRange}) ->
    !velRange or (v >= velRange.lo and v <= velRange.hi)

/**
 * Apply sf2 parameters and envelopes for a set of instrument samples.
 * return an object that can be used to stop the note by passing to noteOff.
 */

// Per-channel GS sound-controller NRPNs (vibrato, TVF, envelope) and the modwheel,
// applied at noteOn as adjustments to the zone's own generator values. Zone records are
// shared across voices, so adjusted values go on shallow copies.
//
// Calibration and its provenance — GM2 defines these NRPNs only as ±64-centered relative
// controls with implementation-defined units, so where a documented reference exists we
// match it, and where none does the constant is calibrated by ear against ZUN's own
// SC-88Pro sequences (the corpus this repertoire ships):
//   vibrato rate   linear in Hz, 0.1 Hz per step around a 5.5 Hz center — MEASURED on
//                  real SC-55mk2 firmware via Nuked-SC55 (offsets −32/+32 tracked to
//                  2.33/8.83 Hz; the log-timecent model borrowed from SpessaSynth had
//                  the wrong curve shape)
//   vibrato depth  2.5 pitch cents per step, additive — MEASURED on SC-55mk2 end to end
//                  (whistle: +46 adds ±90 cents on hardware; 2.5/step through OUR
//                  triangle LFO measures the same ±90 through the same pitch
//                  tracker — the raw 1.75 slope under-measured through our shape). GM2
//                  leaves these units implementation-defined; hardware is the truth.
//   vibrato delay  additive 30 ms per step — MEASURED on SC-55mk2 (+36 moved onset
//                  0.17s → 1.24s); the earlier 2^(step/32) multiplier was a guess
//   cutoff         ±150 filter cents per step — SpessaSynth CC74 modulator (9600/64)
//   resonance      ±0.390625 dB per step — SpessaSynth CC71 modulator (250 cB/64)
//   attack/decay/release  ±56.25 timecents per step, i.e. ×2^(step/21.33) —
//                  SpessaSynth CC72/75 modulators (3600 timecents full-scale)
// A zone with no vibrato of its own gets the measured 5.5Hz Sound Canvas LFO and a
// 0.17s onset (both measured at neutral) when NRPN depth or the modwheel asks for one.
// CC 1 → 50 cents of mod-LFO pitch at full scale is the SF2 2.04 default modulator
// (spec 8.4.2), verbatim.
export adjustSamples := (samples: InstrumentSample[], channel: MidiChannel) ->
  {nrpn, mod} := channel
  {vibRate, vibDepth, vibDelay, cutoff, resonance, attack, decay, release} := nrpn
  neutral := !vibRate and !vibDepth and !vibDelay and !cutoff and !resonance and
    !attack and !decay and !release and !mod
  return samples if neutral

  // ×2^(step·56.25/1200): 3600 timecents across the ±64 range.
  egScale := (offset: number) -> pow 2, offset * 56.25 / 1200

  samples.map (s) ->
    out := {...s}
    if attack  then out.volAttack  = s.volAttack  * egScale attack
    if decay   then out.volDecay   = s.volDecay   * egScale decay
    if release then out.volRelease = s.volRelease * egScale release
    if cutoff or resonance
      out.initialFilterFc = clamp s.initialFilterFc + cutoff * 150, 1500, 13500
      out.initialFilterQ  = clamp s.initialFilterQ + resonance * 0.390625, 0, 30
    if vibRate or vibDepth or vibDelay
      hasOwn := s.vibLfoToPitch > 0
      out.vibLfoToPitch = max 0, s.vibLfoToPitch + vibDepth * 2.5
      if out.vibLfoToPitch > 0
        // Rate is linear in Hz on hardware; freqVibLFO is cents against the 8.176Hz
        // SF2 reference, so convert, offset, convert back.
        baseHz := (hasOwn ? 8.176 * pow(2, s.freqVibLFO / 1200) : 5.5)
        hz := clamp baseHz + vibRate * 0.1, 0.1, 12
        out.freqVibLFO  = 1200 * log2(hz / 8.176)
        out.delayVibLFO = max 0, (hasOwn ? s.delayVibLFO : 0.17) + vibDelay * 0.03
    if mod > 0
      out.modLfoToPitch = s.modLfoToPitch + mod * 50
      // A mod LFO that exists only for the modwheel runs at vibrato speed, not the
      // 8.176Hz SF2 default.
      if !s.modLfoToPitch and !s.modLfoToFilterFc and !s.modLfoToVolume
        out.freqModLFO = -700
    out

// GS drum-setup NRPNs (MSB 0x18-0x1E), per drum note. ZUN's sequences retune and
// remix kits per note — Septette pitches its kick down 5 semitones and pushes the
// snare's level to 120, which is most of the Sound Canvas "boom" — so dropping these
// left drums small and polite.
//   pitch   ±(v-64) semitones on playback rate
//   level   TVA level, ABSOLUTE: amplitude (v/127)^2, where 127 is the kit's own level.
//           It can only attenuate — there is no value that makes a drum louder than the
//           soundfont recorded it.
//
//           MEASURED on a cycle-accurate SC-55mk2 (Nuked-SC55, ROMs in /data/midi/sc55-roms)
//           rather than reasoned about. One kick, NRPN 0x1A swept, peak vs. sending no
//           NRPN at all: 127 → 0.00dB, 120 → -0.93, 110 → -2.43, 100 → -4.12, 80 → -8.09,
//           64 → -11.97, 32 → -24.00, 0 → silence. That is (v/127)^2 to within 0.06dB at
//           every point, and level 127 is indistinguishable from the kit default, on notes
//           36, 38 and 42 alike. Same squared curve as CC7 x CC11 and velocity.
//
//           This replaced `min 2, v/64`, which read the parameter as a multiplier around a
//           64 center and so could amplify by up to +6dB. That was ear-calibrated on one
//           track and it was wrong in the loud direction by 6.4dB on th06_03's kick (level
//           120 over 457 hits), 6.8 on its snare, 7.6 on its tom.
//
//           If you are ever unsure what GS does here, do not reason about it: write a probe
//           MIDI and render it through nuked-sc55-render. Note that the SC-55 ignores an
//           NRPN sent on the same tick as the note it targets — leave real delta time
//           between them or every level reads back as the kit default.
//   pan     absolute (64 = center); 0 means "random" on hardware — left as the kit pans
//   reverb/chorus  per-note send floor alongside the zone's own (drum parts often ride
//           these with the channel CC 91 at 0, as Septette does)
export adjustDrumSamples := (samples: InstrumentSample[], channel: MidiChannel, n: number) ->
  d := channel.drumNrpn[n]
  return samples unless d
  samples.map (s) ->
    out := {...s}
    if d.pitch then out.basePlaybackRate = s.basePlaybackRate * pow 2, d.pitch / 12
    if d.level? then out.initialAttenuation = s.initialAttenuation * pow d.level / 127, 2
    if d.pan then out.pan = clamp (d.pan - 64) / 63, -1, 1
    if d.reverb? then out.reverbEffectsSend = max s.reverbEffectsSend, d.reverb / 127
    if d.chorus? then out.chorusEffectsSend = max s.chorusEffectsSend, d.chorus / 127
    // SF2 has no delay-send generator, so the NRPN is the whole send (U.N. Owen's
    // snare 2 rides this at 90 for its echo).
    if d.delay? then out.delayEffectsSend = max s.delayEffectsSend ?? 0, d.delay / 127
    out

noteOn := (t: number, instrumentSamples: InstrumentSample[], v: number, channel: MidiChannel, context: BaseAudioContext) ->
  dest := context.createGain()

  sources := instrumentSamples.map (instrument) ->
    {
      basePlaybackRate
      delayModLFO
      delayVibLFO
      freqModLFO
      freqVibLFO
      initialAttenuation
      initialFilterQ
      initialFilterFc
      loopEnd
      loopStart
      modEnvToFilterFc
      modEnvToPitch
      modLfoToFilterFc
      modLfoToPitch
      modLfoToVolume
      modSustain
      overrideVelocity
      pan
      sample
      sampleModes
      sampleRate
      startTime
      vibLfoToPitch
      volSustain
    } := instrument

    if overrideVelocity
      v = overrideVelocity

    velocity := (v * v) / (127 * 127) // [0.0000615 - 1.0]

    buffer := contextBufferCache(context, sample, sampleRate)

    bufferSource := context.createBufferSource()
    bufferSource.buffer = buffer
    bufferSource.loop = !!(sampleModes & 1)
    bufferSource.loopStart = loopStart
    bufferSource.loopEnd = loopEnd
    bufferSource.playbackRate.value = basePlaybackRate
    bufferSource.start(t, startTime)

    sourceNodes: AudioScheduledSourceNode[] := [bufferSource]

    volEnvNode := context.createGain()
    volEnvGain := volEnvNode.gain
    applyVolumeEnvelope(volEnvGain, instrument, velocity, t)

    // Filter
    let filter
    if initialFilterQ > 0 or initialFilterFc < 13500
      filter = context.createBiquadFilter()
      filter.type = "lowpass"
      filter.frequency.value = REFERENCE_FREQ
      filter.Q.setValueAtTime(initialFilterQ, t)
      filter.detune.setValueAtTime(initialFilterFc, t)

    // Modulation Envelope
    // Safari < 14.1 doesn't have `ConstantSourceNode`
    let modEnvValue: AudioParam?
    if ((modEnvToFilterFc and filter) or modEnvToPitch) and context.createConstantSource
      modEnvSource := context.createConstantSource()
      modEnvSource.start(t)
      sourceNodes.push modEnvSource

      modDelay  := t + instrument.modDelay
      modAttack := modDelay  + instrument.modAttack
      modHold   := modAttack + instrument.modHold
      modDecay  := modHold   + instrument.modDecay

      modEnvValue = modEnvSource.offset
      modEnvValue.setValueAtTime 0, t
      modEnvValue.setValueAtTime 0, modDelay
      modEnvValue.linearRampToValueAtTime 1, modAttack
      modEnvValue.setValueAtTime 1, modHold
      modEnvValue.linearRampToValueAtTime 1 - modSustain / 100, modDecay

      // ramp using detune
      // 'Filter cutoff frequency is always modified logarithmically, that is the
      // deviation is in cents, semitones, and octaves rather than in Hz.'
      if modEnvToFilterFc and filter
        modEnvToFilterFcNode := context.createGain()
        modEnvSource.connect(modEnvToFilterFcNode)
        modEnvToFilterFcNode.gain.setValueAtTime(modEnvToFilterFc, t)
        modEnvToFilterFcNode.connect(filter.detune)

      if modEnvToPitch
        modEnvToPitchNode := context.createGain()
        modEnvSource.connect(modEnvToPitchNode)
        modEnvToPitchNode.gain.setValueAtTime(modEnvToPitch, t)
        modEnvToPitchNode.connect(bufferSource.detune)

    let dbGainNode: ReturnType<typeof DBWorklet.createDBGainNode>?
    // Live modwheel target — the CC 1 handler below retunes this gain on sounding
    // voices. Only exists when the voice has a mod LFO; a voice born with the wheel at
    // zero on a patch with no mod LFO of its own has nothing to retune (documented gap).
    let modPitchGain: AudioParam?
    // Modulation LFO
    if (modLfoToFilterFc and filter) or modLfoToPitch or modLfoToVolume
      modLFONode := context.createOscillator()
      modLFONode.type = "triangle" // sf2 spec p. 32
      modLFONode.frequency.value = REFERENCE_FREQ
      modLFONode.detune.value = freqModLFO
      modLFONode.start(t + delayModLFO)
      sourceNodes.push modLFONode

      if modLfoToFilterFc and filter
        modLfoToFilterFcNode := context.createGain()
        modLFONode.connect(modLfoToFilterFcNode)
        modLfoToFilterFcNode.gain.setValueAtTime(modLfoToFilterFc, t)
        modLfoToFilterFcNode.connect(filter.detune)

      if modLfoToPitch
        modLfoToPitchNode := context.createGain()
        modLFONode.connect(modLfoToPitchNode)
        modLfoToPitchNode.gain.setValueAtTime(modLfoToPitch, t)
        modLfoToPitchNode.connect(bufferSource.detune)
        modPitchGain = modLfoToPitchNode.gain

      if modLfoToVolume
        modLfoToVolumeNode := context.createGain()
        modLFONode.connect(modLfoToVolumeNode)
        modLfoToVolumeNode.gain.setValueAtTime(modLfoToVolume, t)

        dbGainNode = DBWorklet.createDBGainNode(context)
        modLfoToVolumeNode.connect dbGainNode.db
        volEnvNode.connect dbGainNode

    // Vibrato LFO
    if vibLfoToPitch
      vibLFONode := context.createOscillator()
      vibLFONode.type = "triangle" // sf2 spec p. 33
      vibLFONode.frequency.value = REFERENCE_FREQ
      vibLFONode.detune.value = freqVibLFO
      vibLFONode.start(t + delayVibLFO)
      sourceNodes.push vibLFONode

      vibLfoToPitchNode := context.createGain()
      vibLFONode.connect(vibLfoToPitchNode)
      vibLfoToPitchNode.gain.setValueAtTime(vibLfoToPitch, t)
      vibLfoToPitchNode.connect(bufferSource.detune)

    // pan: channel CC pan and the pan generator sum into one equal-power
    // stage, like FluidSynth's default CC10 -> GEN_PAN modulator
    panner := context.createPanner()
    theta := clamp(channel.pan + pan, -1, 1) * PI / 2
    panner.setPosition(
      sin(theta),
      0,
      cos(theta)
    )

    if filter
      bufferSource.connect(filter)
      filter.connect(volEnvNode)
    else
      bufferSource.connect(volEnvNode)

    if dbGainNode
      dbGainNode.connect panner
    else
      volEnvNode.connect panner

    panner.connect(dest)

    bufferSource.onended = ->
      panner.disconnect()
      dbGainNode?.cleanup()

    bufferSource: bufferSource
    modEnvValue: modEnvValue
    modPitchGain: modPitchGain
    // The zone's own depth, without the wheel's contribution at noteOn — what the live
    // CC 1 handler adds the fresh wheel value back onto.
    modPitchBase: instrument.modLfoToPitch - channel.mod * 50
    sourceNodes: sourceNodes
    volEnvGain: volEnvGain
    output: panner
    velocity: velocity

  // The send is per zone, and a note can sound several zones at once through the one `dest`
  // gain, so the loudest zone's send decides the voice's. Averaging would let a layered
  // patch whose zones disagree end up drier than either zone asked for.
  reverbSend := instrumentSamples.reduce(
    (send, {reverbEffectsSend}) -> max send, reverbEffectsSend
    0
  )
  chorusSend := instrumentSamples.reduce(
    (send, {chorusEffectsSend}) -> max send, chorusEffectsSend
    0
  )
  delaySend := instrumentSamples.reduce(
    (send, {delayEffectsSend}) -> max send, delayEffectsSend ?? 0
    0
  )

  // Need to add everything that noteOff needs to this object
  return {
    connect: dest.connect.bind(dest)
    // Live pan CC moves sounding voices. The channel handler runs before
    // channel.control is updated, so the fresh value arrives as v.
    controlChange: (n: number, v: number, t: number) ->
      // Live modwheel: retune each source's mod-LFO pitch depth (SF2 default modulator,
      // 50 cents full scale) so a swell moves notes already sounding.
      if n is 0x01
        depth := v / 127 * 50
        sources.forEach ({modPitchGain, modPitchBase}) ->
          modPitchGain?.setValueAtTime max(0, modPitchBase + depth), t
        return
      return unless n is 0x0A or n is 0x2A
      channelPan := n is 0x0A ? panToFloat(v) : channel.pan

      sources.forEach ({output}, i) ->
        return unless output.positionX
        theta := clamp(channelPan + instrumentSamples[i].pan, -1, 1) * PI / 2
        output.positionX.setValueAtTime sin(theta), t
        output.positionZ.setValueAtTime cos(theta), t
    instrumentSamples
    reverbSend
    chorusSend
    delaySend
    // Filled by addVoice; live CC 91/93 retunes these send gains on sounding voices.
    fxSends: [] as {cc: number, param: AudioParam, base: number}[]
    mute
    sources
    stopped: undefined as number?
    t
  }

/**
 * Apply release enevelope and stop the note.
 * releaseOverride replaces both envelope release times (exclusive class choke).
 */
noteOff := (t: number, voice: Voice, context: BaseAudioContext, releaseOverride?: number) ->
  {instrumentSamples, sources, t:startTime} := voice

  instrumentSamples.forEach (instrument, i) ->
    {
      bufferSource
      sourceNodes
      modEnvValue
      volEnvGain
      velocity
    } := sources[i]

    {
      volDelay
      volAttack
      volHold
    } := instrument

    volRelease := releaseOverride ?? instrument.volRelease
    modRelease := releaseOverride ?? instrument.modRelease

    // Handle cases where release is at the same deltaTime as noteOn (drum kits)
    // by giving delay and attack a short time to take effect
    envTime := max 3 * defaultMinTime, t - startTime

    currentValue := max NEAR_ZERO, volEnvelopeValue(instrument, envTime, velocity)
    volEnvGain.cancelScheduledValues t
    // linear when rising, exponential when falling
    if envTime < volDelay + volAttack + volHold
      volEnvGain.linearRampToValueAtTime(currentValue, t)
    else
      volEnvGain.exponentialRampToValueAtTime(currentValue, t)
    // volEndTime depends on volume at release time, volRelease is the time
    // corresponding to a 100 dB attenuation
    volEndTime := t + volRelease * max(0, 1 + 20 * log10(currentValue) / 100)
    // exponential in amplitude, linear in dB/s (sf2 spec p. 35 releaseVolEnv)
    volEnvGain.exponentialRampToValueAtTime NEAR_ZERO, volEndTime
    // volEnvGain.linearRampToValueAtTime 0, volEndTime + 1/1024

    // Modulation Envelope Release
    if modEnvValue
      currentValue := modEnvelopeValue(instrument, envTime)
      modEndTime := t + modRelease * currentValue
      modEnvValue.cancelScheduledValues t
      // modEnv change is always linear
      modEnvValue.linearRampToValueAtTime(currentValue, t)
      modEnvValue.linearRampToValueAtTime(0, modEndTime)

    // TODO: set loop to false at time t based on loop mode
    // currently web audio has no clear way to schedule a change to the loop
    // value https://github.com/WebAudio/web-audio-api/issues/2406
    // bufferSource.loop = false
    sourceNodes.forEach (sourceNode) ->
      sourceNode.stop(volEndTime)
    voice.stopped = volEndTime

// DAHDS Volume Envelope
// This is somewhat complicated because to handle the release envelope properly
// we need to apply the partial envelope (either linear or exponential) depending
// on exactly where in the envelope the release occurs
applyVolumeEnvelope := (gain: GainNode['gain'], instrument: InstrumentSample, velocity: number, t: number) ->
  {
    initialAttenuation
    volSustain
  } := instrument

  volDelay  := t + instrument.volDelay
  volAttack := volDelay  + instrument.volAttack
  volHold   := volAttack + instrument.volHold
  volDecay  := volHold   + instrument.volDecay

  // Floored at NEAR_ZERO, never 0: the decay below is an exponential ramp, and an
  // AudioParam rejects an exponential ramp whose target is zero (RangeError) — which a zero
  // gain reaches by several routes (velocity 0 on the immediate path, a GS drum-level NRPN
  // of 0, an attenuation that underflows). A throw out of noteOn is not one lost note: the
  // scheduler's event iterator retries the event it was on, so playback stalls on it.
  peakAmplitude    := max NEAR_ZERO, initialAttenuation * velocity
  sustainAmplitude := max NEAR_ZERO, peakAmplitude * volSustain

  // start at NEAR_ZERO rather than 0 because exponential ramps require a non-zero number
  // mainly for the case where the release envelope triggers and cancels out the
  // linear ramp up
  gain.setValueAtTime(NEAR_ZERO, t)
  gain.setValueAtTime(NEAR_ZERO, volDelay)
  // initial curve is "convex" in dB, linear in amplitude (sf2 spec p. 34 attackVolEnv)
  gain.linearRampToValueAtTime(peakAmplitude, volAttack)
  gain.setValueAtTime(peakAmplitude, volHold)
  // exponential ramp is linear in dB/s (sf2 spec p. 34 decayVolEnv)
  gain.exponentialRampToValueAtTime(sustainAmplitude, volDecay)

  return

// Get the value of the volume envelope value at time t from note start
// This recreates the volume envelope that is applied during note on so that we
// can clear and previous ramps and set the release without a discontinuity
// As far as I know there is no way in the web audio API to add a "null point" to
// the automation to use for the start of future ramps with whatever value the
// parameter has at the time.
volEnvelopeValue := (instrument: InstrumentSample, t: number, velocity: number) ->
  {
    initialAttenuation
    volDelay
    volAttack
    volHold
    volDecay
    volSustain
    sustainVolEnv
  } := instrument

  // Same floor as applyVolumeEnvelope: the decay branch divides by the peak, and a zero
  // peak made the release-time math NaN — noteOff then threw on the ramp, not only noteOn.
  peakAmplitude := max NEAR_ZERO, initialAttenuation * velocity

  if t < volDelay
    return NEAR_ZERO
  t -= volDelay
  if t < volAttack
    // initial curve is "convex" in dB, linear in amplitude (sf2 spec p. 34 attackVolEnv)
    return (t / volAttack) * peakAmplitude
  t -= volAttack
  if t < volHold
    return peakAmplitude
  t -= volHold
  if t < volDecay
    // https://webaudio.github.io/web-audio-api/#dom-audioparam-exponentialramptovalueattime
    // exponential curve in amplitude, linear in dB/s (sf2 spec p. 34 decayVolEnv)
    v0 := peakAmplitude
    v1 := max NEAR_ZERO, peakAmplitude * volSustain

    return v0 * pow(v1 / v0, t / volDecay)

    // db = (t / volDecay) * sustainVolEnv
    // return dbAttenuationToGain db

  return max NEAR_ZERO, peakAmplitude * volSustain

// Get the value of the volume envelope value at time t from note start
modEnvelopeValue := (instrument: InstrumentSample, t: number) ->
  {
    modDelay
    modAttack
    modHold
    modDecay
    modSustain
  } := instrument

  if t < modDelay
    return 0
  t -= modDelay
  if t < modAttack
    return t / modAttack
  t -= modAttack
  if t < modHold
    return 1
  t -= modHold
  if t < modDecay
    return 1 - (t / modDecay) * (modSustain / 100)

  return 1 - modSustain / 100

function mute(this: Voice, time?: number)
  {instrumentSamples, sources, stopped} := @

  instrumentSamples.forEach (instrument, i) =>
    {sourceNodes, output, volEnvGain} := sources[i]

    {context} := output
    t := max time ?? 0, context.currentTime

    // FF doesn't have cancelAndHoldAtTime
    // https://bugzilla.mozilla.org/show_bug.cgi?id=1308431
    if volEnvGain.cancelAndHoldAtTime
      volEnvGain.cancelAndHoldAtTime(t)
    else
      // TODO: ensure envelope continuity
      volEnvGain.cancelScheduledValues(t)

    fadeDuration := 0.01
    volEndTime .= t + fadeDuration

    if stopped?
      volEndTime = min volEndTime, stopped

    volEnvGain.exponentialRampToValueAtTime NEAR_ZERO, volEndTime

    unless stopped?
      @stopped = volEndTime
      sourceNodes.forEach (sourceNode) ->
        sourceNode.stop(volEndTime)

export default SF2Synth

export type Voice = ReturnType<typeof noteOn>

type GeneratorEnumerator =
  'startAddrsOffset' |
  'endAddrsOffset' |
  'startloopAddrsOffset' |
  'endloopAddrsOffset' |
  'startAddrsCoarseOffset' |
  'modLfoToPitch' |
  'vibLfoToPitch' |
  'modEnvToPitch' |
  'initialFilterFc' |
  'initialFilterQ' |
  'modLfoToFilterFc' |
  'modEnvToFilterFc' |
  'endAddrsCoarseOffset' |
  'modLfoToVolume' |
  // undefined, // 14
  'chorusEffectsSend' |
  'reverbEffectsSend' |
  'pan' |
  // undefined, // 18,19,20
  'delayModLFO' |
  'freqModLFO' |
  'delayVibLFO' |
  'freqVibLFO' |
  'delayModEnv' |
  'attackModEnv' |
  'holdModEnv' |
  'decayModEnv' |
  'sustainModEnv' |
  'releaseModEnv' |
  'keynumToModEnvHold' |
  'keynumToModEnvDecay' |
  'delayVolEnv' |
  'attackVolEnv' |
  'holdVolEnv' |
  'decayVolEnv' |
  'sustainVolEnv' |
  'releaseVolEnv' |
  'keynumToVolEnvHold' |
  'keynumToVolEnvDecay' |
  'instrument' |
  // undefined, // 42
  'keyRange' |
  'velRange' |
  'startloopAddrsCoarseOffset' |
  'keynum' |
  'velocity' |
  'initialAttenuation' |
  // undefined, // 49
  'endloopAddrsCoarseOffset' |
  'coarseTune' |
  'fineTune' |
  'sampleID' |
  'sampleModes' |
    // undefined, // 55
  'scaleTuning' |
  'exclusiveClass' |
  'overridingRootKey'

export type InstrumentSample = {
  basePlaybackRate: number
  /** 0-1 send level to the shared chorus bus. Read but not yet consumed. */
  chorusEffectsSend: number
  /** 0-1 send level to the shared reverb bus. */
  reverbEffectsSend: number
  /** 0-1 send level to the GS delay bus. No SF2 generator exists for this — it is set
  only by the drum-setup NRPN 0x1F (`adjustDrumSamples`), hence optional. */
  delayEffectsSend?: number
  delayModLFO: number
  delayVibLFO: number
  end: number
  exclusiveClass: number
  freqModLFO: number
  freqVibLFO: number
  initialAttenuation: number
  initialFilterQ: number
  initialFilterFc: number
  loopEnd: number
  loopStart: number
  modEnvToFilterFc: number
  modEnvToPitch: number
  modLfoToFilterFc: number
  modLfoToPitch: number
  modLfoToVolume: number
  modAttack: number
  modDecay: number
  modDelay: number
  modHold: number
  modSustain: number
  overrideVelocity: number | null
  pan: number
  sample: Int16Array
  sampleModes: number
  sampleRate: number
  startTime: number
  velRange: { lo: number, hi: number }
  vibLfoToPitch: number
  volSustain: number
  modRelease: number
  volDelay: number
  volAttack: number
  volDecay: number
  volHold: number
  volRelease: number
  sustainVolEnv: number

  presetGenerator: BagModGen
  generator: BagModGen
}

export { SpessaSynth } from ./spessa-synth.civet
export type { SynthTaps } from ./spessa-synth.civet
/** Either engine behind the player: the node-graph SF2Synth or the worklet SpessaSynth spike. */
export type SynthEngine = SF2Synth | import("./spessa-synth.civet").SpessaSynth
